/**
 * @license
 * @copyright Copyright Motili Inc., 2023 All Rights Reserved
 */

import { FileInput, Heading1, LoadingSpinner, Paragraph } from '@motili/ui';
import { Fragment, ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { setDocuments } from '../../../common/redux/ducks/contractorRegistrationForm';
import { useAppDispatch, useAppSelector } from '../../../common/redux/hooks';
import CenteredContainer from '../../../components/layout/CenteredContainer';
import FileCard from './FileCard';
import MultipleFilesUpload, { MultipleFilesEntry } from './MultipleFilesUpload';
import { DOCUMENT_UPLOAD_FIELDS } from '../../../common/constants';
import FileUploadFormFields, { FormFieldValues } from './FileUploadFormFields';
import StyledButton from '../../../components/styled/StyledButton';
import ButtonContainer from '../../../components/layout/ButtonContainer';
import {
    DOCUMENT_TYPES,
    DocumentLink,
    RequiredFormFields,
} from '../../../common/types';

const MULTIPLE_FILE_DOCUMENT_TYPES: Set<DOCUMENT_TYPES> = new Set([
    DOCUMENT_TYPES.taxSalesCertificate,
    DOCUMENT_TYPES.tradeLicenses,
]);

interface FileUploadProps {
    handleBackClick: () => void;
    handleFileUpload: (
        fileName: string,
        file: File,
        formFieldValues: FormFieldValues
    ) => Promise<unknown>;
    handleMultipleFilesUpload: (
        fileNameId: string,
        files: MultipleFilesEntry[]
    ) => Promise<unknown>;
}

interface DocumentUploadTypes {
    paragraphText?: string;
    paragraphContent?: {
        intro: string;
        listType: string;
        listItems: string[];
        outro: string;
    };
    links?: DocumentLink[];
    requiredFormFields: string[];
}

export default function FileUpload({
    handleBackClick,
    handleFileUpload,
    handleMultipleFilesUpload,
}: FileUploadProps): ReactElement {
    const dispatch = useAppDispatch();
    const { fileName: fileNameId, label } = useParams<{
        fileName: DOCUMENT_TYPES;
        label: string;
    }>();
    const {
        paragraphText,
        paragraphContent,
        links,
        requiredFormFields,
    }: DocumentUploadTypes =
        DOCUMENT_UPLOAD_FIELDS[
            fileNameId as keyof typeof DOCUMENT_UPLOAD_FIELDS
        ];

    const documents = useAppSelector(
        ({ contractorRegistration }) =>
            contractorRegistration.contractor.documents
    );
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [formFieldValues, setFormFieldValues] = useState<FormFieldValues>({});

    const existingDocEntry =
        documents[fileNameId as keyof typeof documents]?.[0];

    const isFormValid =
        !!file &&
        requiredFormFields.every(
            field => !!formFieldValues[field as keyof FormFieldValues]
        );

    const onDropAccepted = (files: File[]) => {
        setFile(files[0]);
    };
    const handleTrashClick = (isExisting: boolean) => {
        if (isExisting) {
            dispatch(
                setDocuments({
                    ...documents,
                    [fileNameId as keyof typeof documents]: [],
                })
            );
        } else {
            setFile(null);
        }
    };
    const handleSubmitClick = async () => {
        setLoading(true);
        if (file && fileNameId) {
            await handleFileUpload(fileNameId, file, formFieldValues);
        }
        setLoading(false);
        handleBackClick();
    };

    const renderFileCard = () => {
        if (file) {
            return (
                <FileCard
                    fileName={file.name}
                    handleTrashClick={() => handleTrashClick(false)}
                />
            );
        }
        if (existingDocEntry) {
            const existingFileName =
                'name' in existingDocEntry
                    ? existingDocEntry.name
                    : existingDocEntry.fileName;
            return (
                <FileCard
                    fileName={existingFileName}
                    handleTrashClick={() => handleTrashClick(true)}
                />
            );
        }
        return null;
    };

    if (fileNameId && MULTIPLE_FILE_DOCUMENT_TYPES.has(fileNameId)) {
        return (
            <MultipleFilesUpload
                fileNameId={fileNameId}
                label={label || ''}
                paragraphText={paragraphText || ''}
                handleBackClick={handleBackClick}
                handleMultipleFilesUpload={handleMultipleFilesUpload}
            />
        );
    }
    return (
        <CenteredContainer hideProgressStepper>
            <StyledLink to='/contractor/documents'>
                <i className='fa fa-arrow-left' aria-hidden='true' />
                <span>{`Back to Documents`}</span>
            </StyledLink>
            <Heading1>{`${label}`}</Heading1>
            <div style={{ marginBottom: 20, fontWeight: 300 }}>
                {paragraphContent ? (
                    <>
                        <Paragraph>{paragraphContent.intro}</Paragraph>
                        {paragraphContent.listType === 'ordered' ? (
                            <ol>
                                {paragraphContent.listItems.map(item => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ol>
                        ) : (
                            <ul>
                                {paragraphContent.listItems.map(item => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        )}
                        <Paragraph>{paragraphContent.outro}</Paragraph>
                    </>
                ) : (
                    <Paragraph>{paragraphText}</Paragraph>
                )}
            </div>
            <FileInput
                accept={{
                    'image/jpeg': [],
                    'image/png': [],
                    'application/pdf': [],
                    '.doc': [],
                    '.docx': [],
                }}
                onDrop={() => {}}
                onDropAccepted={onDropAccepted}
                onDropRejected={() => {}}
                additionalInfoText={
                    'Accepted file types are .png, .pdf, .jpg, .doc, and .docx.'
                }
            />
            {links && (
                <StyledLinkSection>
                    <StyledLinkHeading>
                        Secretary of State Links Per State
                    </StyledLinkHeading>
                    {links.map(({ label: linkLabel, url }, index) => (
                        <Fragment key={linkLabel}>
                            {index > 0 && (
                                <StyledLinkSeparator> | </StyledLinkSeparator>
                            )}
                            <a
                                href={url}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {linkLabel}
                            </a>
                        </Fragment>
                    ))}
                </StyledLinkSection>
            )}
            {renderFileCard()}
            {file && (
                <FileUploadFormFields
                    fileNameId={fileNameId || ''}
                    onChange={setFormFieldValues}
                    fullWidthFields={[RequiredFormFields.tradeType]}
                />
            )}
            {loading && <LoadingSpinner fullscreen busy={loading} />}
            <ButtonContainer>
                <StyledButton
                    variant={'primary'}
                    disabled={!isFormValid}
                    onClick={handleSubmitClick}
                    buttonStyle={{ minWidth: 140 }}
                >
                    Confirm & Save
                </StyledButton>
            </ButtonContainer>
        </CenteredContainer>
    );
}

const StyledLink = styled(Link)`
    color: ${({ theme }) => theme.color('primary')};
    text-decoration: none;

    .fa {
        font-size: 14px;
        padding-right: 10px;
    }

    &:hover {
        color: ${({ theme }) => theme.color('hover')};
    }

    &:hover span {
        text-decoration: underline;
    }
`;

const StyledLinkSection = styled.div`
    margin-top: 24px;
    font-size: 14px;
    line-height: 1.6;

    a {
        color: ${({ theme }) => theme.color('primary')};
        font-weight: 500;
        text-decoration: none;
    }

    a:hover {
        color: ${({ theme }) => theme.color('hover')};
        text-decoration: underline;
    }
`;

const StyledLinkHeading = styled.div`
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color('black')};
`;

const StyledLinkSeparator = styled.span`
    color: ${({ theme }) => theme.color('black')};
`;
